import React, { useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { getPerformanceData } from '../redux/reducer';
import i18next from '../translations/i18n';
import utils from '../services/Utils';
import Loader from './Loader';

const ANNUAL_RETURNS = 'annualReturns';
const CUMULATIVE_RETURNS = 'cumulativeReturns';
const CALENDER_PERFORMANCE = 'calenderPerformance';
const formatPercentage = value => (value != null ? `${value}\u00A0%` : '-');

const getCalenderTableData = tabular => {
  const filtered = tabular
    .filter(t => t.rorWindow === 'calYE')
    .sort((a, b) => new Date(b.dt) - new Date(a.dt)); // latest first

  const grouped = filtered.reduce(
    (acc, item) => {
      const year = new Date(item.dt).getFullYear();

      // Init if not exists
      if (!acc.map[year]) {
        acc.map[year] = { year };
        acc.years.push(year);
      }

      if (item.wtTypeID === 1) {
        acc.map[year].product = utils.roundToPercentage(item.windowRoR, 2);
      } else {
        acc.map[year].bmk = utils.roundToPercentage(item.windowRoR, 2);
      }

      return acc;
    },
    { map: {}, years: [] },
  );

  return grouped.years
    .sort() // ascending
    .map(year => grouped.map[year]);
};

const getAnnualisedReturnsTableData = tabular => {
  const periodKeys = ['yr1Ror', 'yr3Ror', 'yr5Ror', 'yr10Ror', 'cumRorAnn'];

  return periodKeys.reduce((acc, key) => {
    const entries = tabular.filter(d => d.rorWindow === key);
    const product = entries.find(p => p.wtTypeID === 1);
    const bmk = entries.find(p => p.wtTypeID !== 1);

    const productVal = utils.roundToPercentage(product?.windowRoR ?? null, 2);
    const bmkVal = utils.roundToPercentage(bmk?.windowRoR ?? null, 2);

    // Only push if either value is available
    if (productVal != null || bmkVal != null) {
      acc.push({
        year:
          key !== 'cumRorAnn'
            ? `${key.replace('yr', '').replace('Ror', '')}Y`
            : i18next.t('cumRorAnn').toUpperCase(),
        product: productVal,
        bmk: bmkVal,
        key,
      });
    }

    return acc;
  }, []);
};

const getCumulativeReturnsTableData = tabular => {
  const periodKeys = ['ytd', 'mtd', 'mos3Ror', 'mos6Ror', 'cumRor'];

  return periodKeys.reduce((acc, period) => {
    const pair = tabular.filter(d => d.rorWindow === period);

    const product = pair.find(p => p.wtTypeID === 1);
    const bmk = pair.find(p => p.wtTypeID !== 1);

    const productVal = utils.roundToPercentage(product?.windowRoR ?? null, 2);
    const bmkVal = utils.roundToPercentage(bmk?.windowRoR ?? null, 2);

    if (productVal != null || bmkVal != null) {
      acc.push({
        label: period.toUpperCase(),
        product: productVal,
        bmk: bmkVal,
      });
    }

    return acc;
  }, []);
};

export const getPerformanceTabData = tabular => {
  const annualReturns = getAnnualisedReturnsTableData(tabular);
  const cumulativeReturns = getCumulativeReturnsTableData(tabular);
  const calenderPerformance = getCalenderTableData(tabular);

  let defaultTab = null;
  if (annualReturns.length > 0) {
    defaultTab = ANNUAL_RETURNS;
  } else if (cumulativeReturns.length > 0) {
    defaultTab = CUMULATIVE_RETURNS;
  } else if (calenderPerformance.length > 0) {
    defaultTab = CALENDER_PERFORMANCE;
  }

  return {
    annualReturns,
    cumulativeReturns,
    calenderPerformance,
    defaultTab,
  };
};

const AnnualReturnsTable = ({ annualReturns, fundName, indexName, getAsOfDate }) => (
  <div className="tab-pane fade in active">
    <div className="table-scroll">
      <table className="performance-table">
        <thead>
          <tr>
            <th>{i18next.t('PRODUCT')}</th>
            {annualReturns.map(ap => (
              <th key={ap.year}>{i18next.t(ap.year)}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {fundName && (
            <tr>
              <td>{fundName}</td>
              {annualReturns.map(ap => (
                <td key={ap.year}>{formatPercentage(ap.product)}</td>
              ))}
            </tr>
          )}
          {indexName && (
            <tr>
              <td>{indexName}</td>
              {annualReturns.map(ap => (
                <td key={ap.key}>{formatPercentage(ap.bmk)}</td>
              ))}
            </tr>
          )}
        </tbody>
      </table>
    </div>
    <div className="as-of-date">
      <span>{`${i18next.t('AS_OF')}`}</span>
      <span className="date-value "> {` ${getAsOfDate('monthEnd')}`}</span>
    </div>
  </div>
);

const CumulativeReturnsTable = ({ cumulativeReturns, fundName, indexName, getAsOfDate }) => (
  <div className="tab-pane fade in active">
    <div className="table-scroll">
      <table className="performance-table">
        <thead>
          <tr>
            <th>{i18next.t('PRODUCT')}</th>
            {cumulativeReturns.map(cp => (
              <th key={cp.label}>{i18next.t(cp.label)}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {fundName && (
            <tr>
              <td>{fundName}</td>
              {cumulativeReturns.map(cp => (
                <td key={cp.label}>{formatPercentage(cp.product)}</td>
              ))}
            </tr>
          )}
          {indexName && (
            <tr>
              <td>{indexName}</td>
              {cumulativeReturns.map(cp => (
                <td key={cp.label}>{formatPercentage(cp.bmk)}</td>
              ))}
            </tr>
          )}
        </tbody>
      </table>
    </div>
    <div className="as-of-date">
      <span>{`${i18next.t('AS_OF')}`}</span>
      <span className="date-value "> {` ${getAsOfDate('monthEnd')}`}</span>
    </div>
  </div>
);

const CalendarPerformanceTable = ({ calenderPerformance, fundName, indexName, getAsOfDate }) => (
  <div className="tab-pane fade in active">
    <div className="table-scroll">
      <table className="performance-table">
        <thead>
          <tr>
            <th>{i18next.t('PRODUCT')}</th>
            {calenderPerformance.map(cp => (
              <th key={cp.year}>{cp.year}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {fundName && (
            <tr>
              <td>{fundName}</td>
              {calenderPerformance.map(cp => (
                <td key={cp.year}>{formatPercentage(cp.product)}</td>
              ))}
            </tr>
          )}
          {indexName && (
            <tr>
              <td>{indexName}</td>
              {calenderPerformance.map(cp => (
                <td key={cp.year}>{formatPercentage(cp.bmk)}</td>
              ))}
            </tr>
          )}
        </tbody>
      </table>
    </div>
    <div className="as-of-date">
      <span>{`${i18next.t('AS_OF')}`}</span>
      <span className="date-value "> {` ${getAsOfDate('calYearEnd')}`}</span>
    </div>
  </div>
);

const useFetchTabularPerformanceData = dispatch => {
  useEffect(() => {
    dispatch(getPerformanceData({ entity: window.performanceChart?.tickerCode, mode: 'tabular' }));
  }, [dispatch]);
};

export const PerformanceTabs = ({
  activeTab,
  setActiveTab,
  annualReturns,
  cumulativeReturns,
  calenderPerformance,
}) => (
  <ul className="nav nav-tabs table-tabs">
    {annualReturns.length > 0 && (
      <li className={`tab-responsive ${activeTab === ANNUAL_RETURNS ? 'active' : ''}`}>
        <button type="button" className="nav-link" onClick={() => setActiveTab(ANNUAL_RETURNS)}>
          {i18next.t('ANNUALISED_RETURN')}
        </button>
      </li>
    )}
    {cumulativeReturns.length > 0 && (
      <li className={`tab-responsive ${activeTab === CUMULATIVE_RETURNS ? 'active' : ''}`}>
        <button type="button" className="nav-link" onClick={() => setActiveTab(CUMULATIVE_RETURNS)}>
          {i18next.t('CUMULATIVE_PERFORMANCE')}
        </button>
      </li>
    )}
    {calenderPerformance.length > 0 && (
      <li className={`tab-responsive ${activeTab === CALENDER_PERFORMANCE ? 'active' : ''}`}>
        <button
          type="button"
          className="nav-link"
          onClick={() => setActiveTab(CALENDER_PERFORMANCE)}
        >
          {i18next.t('CALENDAR_PERFORMANCE')}
        </button>
      </li>
    )}
  </ul>
);

// Main component
const PerformanceGrowthTable = ({ activeTab, tableData }) => {
  const dispatch = useDispatch();

  const {
    historicalPerformanceData: { tabular = [] },
    fundName,
    indexName,
  } = useSelector(state => state.app);

  const getAsOfDate = windowTag => {
    const dates = tabular.filter(d => d.windowTag === windowTag).map(d => new Date(d.dt));
    if (dates.length === 0) {
      return null;
    }
    const latestDate = new Date(Math.max(...dates));
    const day = String(latestDate.getDate()).padStart(2, '0');
    const month = String(latestDate.getMonth() + 1).padStart(2, '0');
    const year = latestDate.getFullYear();
    return `${day}-${month}-${year}`;
  };

  const { annualReturns, cumulativeReturns, calenderPerformance } = tableData;

  useFetchTabularPerformanceData(dispatch);

  if (tabular.length === 0) {
    return <Loader />;
  }
  return (
    <div className="table-container">
      <div className="tab-content">
        {activeTab === ANNUAL_RETURNS && annualReturns.length > 0 && (
          <AnnualReturnsTable
            annualReturns={annualReturns}
            fundName={fundName}
            indexName={indexName}
            getAsOfDate={getAsOfDate}
          />
        )}
        {activeTab === CUMULATIVE_RETURNS && cumulativeReturns.length > 0 && (
          <CumulativeReturnsTable
            cumulativeReturns={cumulativeReturns}
            fundName={fundName}
            indexName={indexName}
            getAsOfDate={getAsOfDate}
          />
        )}
        {activeTab === CALENDER_PERFORMANCE && calenderPerformance.length > 0 && (
          <CalendarPerformanceTable
            calenderPerformance={calenderPerformance}
            fundName={fundName}
            indexName={indexName}
            getAsOfDate={getAsOfDate}
          />
        )}
      </div>
    </div>
  );
};

export default PerformanceGrowthTable;
